import { useEffect, useState, useRef, useCallback } from 'react';
import {
  Button,
  TextField,
  Modal,
  Slide,
  IconButton,
  Box,
  Autocomplete,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import './EditSimRequestDrawer.scss';
import SuccessIcon from '../../assets/images/success-check-circle.svg';
import CalendarIcon from '../../assets/images/Calendar.svg';
import DateRangePicker from '../DateRangePicker/DateRangePicker';

interface Props {
  show: boolean;
  setToggle: (show: boolean) => void;
  selectedRow: any;
  onSave: (updatedRow: any) => void;
  adjacentRanges?: {
    prev?: { open: string; close: string };
    next?: { open: string; close: string };
  };
}

export default function EditSimRequestDrawer({
  show,
  setToggle,
  selectedRow,
  onSave,
  adjacentRanges
}: Props) {
  // Extend dayjs with UTC plugin
  dayjs.extend(utc);

  const [year, setYear] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  // const [equipment, setEquipment] = useState<string>();
  const [submissionCloseTime, setSubmissionCloseTime] = useState<string>('');
  const [multiLineInput, setMultiLineInput] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [sendDate, setSendDate] = useState<Dayjs | null>(null);
  const [sendDateAnchorEl, setSendDateAnchorEl] =
    useState<HTMLDivElement | null>(null);
  const sendDateAnchorRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<string>('');
  const [dateRangeError, setDateRangeError] = useState<string>('');

  const timeOptions = [
    '0700 ET',
    '0800 ET',
    '0900 ET',
    '1000 ET',
    '1100 ET',
    '1200 ET',
    '1300 ET',
    '1400 ET',
    '1500 ET',
    '1600 ET',
    '1700 ET'
  ];

  useEffect(() => {
    if (selectedRow) {
      setYear(selectedRow.simYear || '');
      setMonth(selectedRow.simMonth || '');
      setSubmissionCloseTime(
        parseAPITimeToDisplayFormat(selectedRow.simCloseDateTime || '')
      );
      // setEquipment(selectedRow.daterange || 'December 15 - 20');
      setMultiLineInput(selectedRow.description || '');
      setStartDate(
        selectedRow.simOpenDateTime ? dayjs(selectedRow.simOpenDateTime) : null
      );
      setEndDate(
        selectedRow.simCloseDate ? dayjs(selectedRow.simCloseDate) : null
      );
      setSendDate(
        selectedRow.simSendDate ? dayjs(selectedRow.simSendDate) : null
      );
      setStatus(selectedRow.simStatus || '');
    }
  }, [selectedRow]);

  const handleCancel = () => {
    setToggle(false);
  };

  const formatDateTimeForAPI = (date: Dayjs | null, time?: string): string => {
    if (!date) return '';

    if (time && time.includes('ET')) {
      const timeOnly = time.replace(' ET', '');
      const hours = timeOnly.substring(0, 2);
      const minutes = timeOnly.substring(2, 4);
      return date
        .hour(parseInt(hours))
        .minute(parseInt(minutes))
        .format('YYYY-MM-DD HH:mm:ss');
    }

    return date.hour(9).minute(0).second(0).format('YYYY-MM-DD HH:mm:ss');
  };

  const parseAPITimeToDisplayFormat = (apiDateTime: string): string => {
    if (!apiDateTime) return '';

    // Parse the UTC datetime string explicitly as UTC
    const utcDate = dayjs.utc(apiDateTime);
    if (!utcDate.isValid()) return apiDateTime; // Return original if invalid

    // Convert UTC to Eastern Time
    // Eastern Time is UTC-5 (EST) or UTC-4 (EDT during daylight saving time)
    // For September 25, 2025, it should be EDT (UTC-4)
    const easternTime = utcDate.utcOffset(-4 * 60); // -4 hours in minutes

    // Extract hour and minute, format as HHMM ET
    const hours = easternTime.hour().toString().padStart(2, '0');
    const minutes = easternTime.minute().toString().padStart(2, '0');
    return `${hours}${minutes} ET`;
  };

  const getMonthNumber = (monthName: string): string => {
    const months = [
      '',
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];
    const monthIndex = months.indexOf(monthName);
    return monthIndex > 0 ? monthIndex.toString() : '1';
  };

  const handleSave = () => {
    const requestData = {
      simRequestID: selectedRow?.id || 0,
      simYear: parseInt(year) || dayjs().year(),
      simMonth: getMonthNumber(month),
      simOpenDateTime: formatDateTimeForAPI(startDate),
      simCloseDateTime: formatDateTimeForAPI(endDate, submissionCloseTime),
      simSendDate: formatDateTimeForAPI(sendDate),
      comment: multiLineInput || ''
    };

    onSave(requestData);
    setToggle(false);
  };

  const handleDateChange = (start: Dayjs | null, end: Dayjs | null) => {
    setStartDate(start);
    setEndDate(end);
    setDateRangeError(validateOverlap(start, end));
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSendDateClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setSendDateAnchorEl(event.currentTarget);
  };

  const handleSendDateClose = () => {
    setSendDateAnchorEl(null);
  };

  const handleSendDateChange = (start: Dayjs | null, end: Dayjs | null) => {
    setSendDate(start);
    handleSendDateClose();
  };

  const formatDateRange = () => {
    if (startDate && endDate) {
      if (startDate.month() === endDate.month()) {
        return `${startDate.format('MMMM DD')} - ${endDate.format('DD')}`;
      }
      return `${startDate.format('MMMM DD')} - ${endDate.format('MMMM DD')}`;
    }
    return 'Select Date Range';
  };

  const validateOverlap = useCallback(
    (start: Dayjs | null, end: Dayjs | null): string => {
      if (!start || !end) return '';

      const rangesToCheck: Array<{
        label: 'previous' | 'next';
        open?: string;
        close?: string;
      }> = [
        {
          label: 'previous',
          open: adjacentRanges?.prev?.open,
          close: adjacentRanges?.prev?.close
        },
        {
          label: 'next',
          open: adjacentRanges?.next?.open,
          close: adjacentRanges?.next?.close
        }
      ];

      const overlaps = (s1: Dayjs, e1: Dayjs, s2?: string, e2?: string) => {
        if (!s2 || !e2) return false;
        const start2 = dayjs(s2);
        const end2 = dayjs(e2);
        if (!start2.isValid() || !end2.isValid()) return false;
        return !(e1.isBefore(start2, 'day') || s1.isAfter(end2, 'day'));
      };

      for (const r of rangesToCheck) {
        if (overlaps(start, end, r.open, r.close)) {
          return `This date range overlaps with ${r.label} submission period`;
        }
      }

      return '';
    },
    [adjacentRanges]
  );

  useEffect(() => {
    // Validate on initial load and whenever inputs change
    setDateRangeError(validateOverlap(startDate, endDate));
  }, [startDate, endDate, validateOverlap]);

  return (
    <Modal
      open={show}
      onClose={() => setToggle(false)}
      closeAfterTransition
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end'
      }}
    >
      <Slide direction="left" in={show} mountOnEnter unmountOnExit>
        <Box className="edit-sim-request-drawer">
          <div className="drawer-content">
            <div className="drawer-header">
              <h2>Edit</h2>
              <IconButton
                className="close-button"
                onClick={() => setToggle(false)}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </div>

            <div className="drawer-body">
              <div className="drawer-year-month-container">
                <span>{year}</span>
                <span>{month}</span>
              </div>

              <div
                className={`date-range-picker-field ${dateRangeError ? 'error' : ''}`}
                onClick={handleClick}
                ref={anchorRef}
              >
                <p className="date-range-content">{formatDateRange()}</p>
                <img src={CalendarIcon} alt="calendar" />
              </div>
              {dateRangeError && (
                <p className="date-range-error">{dateRangeError}</p>
              )}

              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onDateChange={handleDateChange}
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorRef={anchorRef}
                selectionMode="range"
                popoverWidth="300px"
              />

              <div
                className="send-date-field"
                onClick={handleSendDateClick}
                ref={sendDateAnchorRef}
              >
                <div className="input-box">
                  <div className="content">
                    <p className="send-date-label">Choose Send Date</p>
                    <p className="send-date-content">
                      {sendDate ? sendDate.format('MMMM DD') : 'Select Date'}
                    </p>
                  </div>
                  <i className="icon-chevron-thin-down" />
                </div>
              </div>

              <DateRangePicker
                startDate={sendDate}
                endDate={sendDate}
                onDateChange={handleSendDateChange}
                open={Boolean(sendDateAnchorEl)}
                anchorEl={sendDateAnchorEl}
                onClose={handleSendDateClose}
                anchorRef={sendDateAnchorRef}
                selectionMode="single"
                popoverWidth="300px"
              />

              <div className="closetime-field">
                <Autocomplete
                  value={submissionCloseTime || undefined}
                  onChange={(event, newValue) => {
                    setSubmissionCloseTime(newValue || '');
                  }}
                  options={timeOptions}
                  getOptionLabel={(option) => option}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Submission Close Time"
                      variant="outlined"
                      InputLabelProps={{
                        style: { fontSize: '1.4rem' }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        inputProps: {
                          ...params.inputProps,
                          style: {
                            paddingLeft: 0,
                            fontSize: '1.4rem',
                            fontWeight: 600
                          }
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Typography
                      {...props}
                      key={option}
                      style={{ fontSize: '1.4rem', display: 'block' }}
                    >
                      {option}
                    </Typography>
                  )}
                  noOptionsText={
                    <Typography
                      style={{
                        fontSize: '1.4rem',
                        display: 'block',
                        color: 'black'
                      }}
                    >
                      No options available
                    </Typography>
                  }
                  popupIcon={null}
                  freeSolo={false}
                  disableClearable
                />
              </div>

              <div className="status-field">
                <div className="input-box">
                  <p className="status-label">Status</p>
                  <p className="status-content">{status}</p>
                </div>
                <img src={SuccessIcon} alt="" />
              </div>

              <div className="multiline-input-field">
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  variant="outlined"
                  placeholder="Note (optional)"
                  value={multiLineInput}
                  onChange={(event) => setMultiLineInput(event.target.value)}
                  InputProps={{
                    style: {
                      fontSize: '1.4rem',
                      fontWeight: 400,
                      color: '#333',
                      backgroundColor: 'white'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      border: '1px solid #c4c4c4',
                      borderRadius: '0px',
                      '&:hover': {
                        '& > fieldset': {
                          borderColor: '#666'
                        }
                      },
                      '&.Mui-focused': {
                        '& > fieldset': {
                          borderColor: '#666',
                          borderWidth: '1px'
                        }
                      },
                      '& > fieldset': {
                        border: 'none'
                      }
                    }
                  }}
                />
              </div>

              <div className="bottom__buttons">
                <Button className="cancel_button" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  className="save_button"
                  onClick={handleSave}
                  disabled={Boolean(dateRangeError)}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </Box>
      </Slide>
    </Modal>
  );
}
